import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as adminApi from '@/api/admin';
import type { SalesPlatform } from '@/api/admin';
import {
  adminProductKeys,
  refreshPriceRow,
  type PriceRowRefreshOptions,
} from './useAdminProducts';

// ═══════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════

export const adminDecorationMethodKeys = {
  all: ['admin', 'decoration-methods'] as const,
  lists: () => [...adminDecorationMethodKeys.all, 'list'] as const,
};

// ═══════════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════════

/**
 * Hook to fetch the decoration method catalog with Phoenix ids and the
 * per-platform base prices.
 */
export function useAdminDecorationMethods() {
  return useQuery({
    queryKey: adminDecorationMethodKeys.lists(),
    queryFn: adminApi.getAdminDecorationMethods,
  });
}

// ═══════════════════════════════════════════════════════════════════
// MUTATIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Hook to set or remove a method's base price on one platform.
 * Passing null removes the price row (unpriced); 0 is a real price.
 */
export function useSetDecorationBasePrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      methodId,
      platform,
      price,
    }: {
      methodId: string;
      platform: SalesPlatform;
      price: number | null;
    }) => adminApi.setDecorationBasePrice(methodId, platform, price),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminDecorationMethodKeys.all,
      });
      // Product detail views show base prices as the override reference.
      queryClient.invalidateQueries({ queryKey: adminProductKeys.details() });
    },
  });
}

/**
 * Hook to assign a decoration method to a position.
 * Assignments take effect immediately in the Creator and Direct Sale flows,
 * so the Creator's product cache is invalidated as well.
 */
export function useAssignDecorationMethod(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      positionId,
      methodId,
    }: {
      positionId: string;
      methodId: string;
    }) => adminApi.assignDecorationMethod(positionId, methodId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook to remove a decoration method from a position.
 */
export function useUnassignDecorationMethod(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      positionId,
      methodId,
    }: {
      positionId: string;
      methodId: string;
    }) => adminApi.unassignDecorationMethod(positionId, methodId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook to mark an assigned method as the position default.
 */
export function useSetDefaultDecorationMethod(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      positionId,
      methodId,
    }: {
      positionId: string;
      methodId: string;
    }) => adminApi.setDefaultDecorationMethod(positionId, methodId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook to set or reset a product's decoration price override.
 * Passing null removes the override, resetting to the platform base price.
 * On the price table (with `priceRowParams`) the product's row is
 * refreshed in place as well.
 */
export function useSetProductDecorationPrice(
  productId: string,
  options: PriceRowRefreshOptions = {}
) {
  const queryClient = useQueryClient();
  const { priceRowParams } = options;

  return useMutation({
    mutationFn: ({
      methodId,
      platform,
      price,
    }: {
      methodId: string;
      platform: SalesPlatform;
      price: number | null;
    }) =>
      adminApi.setProductDecorationPrice(productId, methodId, platform, price),
    onSuccess: async () => {
      if (priceRowParams) {
        await refreshPriceRow(queryClient, productId, priceRowParams);
      }
      queryClient.invalidateQueries({
        queryKey: adminProductKeys.detail(productId),
      });
    },
  });
}
